import { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import CloseIcon from '@mui/icons-material/Close';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../branding/BrandLogo';
import {
  INSTALL_PROMPT_NEVER_ASK_KEY,
  INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
  IOS_INSTALL_PROMPT_NEVER_ASK_KEY,
  IOS_INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
  LEGACY_INSTALL_PROMPT_DISMISSED_AT_KEY,
  LEGACY_IOS_INSTALL_PROMPT_DISMISSED_AT_KEY,
  clearInstallPromptDismissals,
  isInstallPromptAllowed,
  migrateLegacyDismissal,
  permanentlyDismissInstallPrompt,
  snoozeInstallPrompt,
} from './installPromptStorage';

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIos = () => {
  const userAgent = window.navigator.userAgent || '';
  const platform = window.navigator.platform || '';

  return (
    /iPad|iPhone|iPod/.test(userAgent) ||
    (platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
  );
};

const installPromptAllowed = () =>
  isInstallPromptAllowed({
    neverAskKey: INSTALL_PROMPT_NEVER_ASK_KEY,
    untilKey: INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
  });

const iosInstallPromptAllowed = () =>
  isInstallPromptAllowed({
    neverAskKey: IOS_INSTALL_PROMPT_NEVER_ASK_KEY,
    untilKey: IOS_INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
  });

const InstallPrompt = () => {
  const { t } = useTranslation();
  const [installEvent, setInstallEvent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosVisible, setIosVisible] = useState(false);

  const canPrompt = useMemo(
    () => Boolean(installEvent) && visible && !isStandalone(),
    [installEvent, visible]
  );
  const canShowIosGuide = iosVisible && !canPrompt && !isStandalone();

  useEffect(() => {
    migrateLegacyDismissal({
      legacyKey: LEGACY_INSTALL_PROMPT_DISMISSED_AT_KEY,
      untilKey: INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
    });
    migrateLegacyDismissal({
      legacyKey: LEGACY_IOS_INSTALL_PROMPT_DISMISSED_AT_KEY,
      untilKey: IOS_INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
    });

    if (isIos() && !isStandalone() && iosInstallPromptAllowed()) {
      setIosVisible(true);
    }

    const refreshVisibility = () => {
      if (isStandalone()) {
        setVisible(false);
        setIosVisible(false);
        return;
      }

      if (installEvent && installPromptAllowed()) {
        setVisible(true);
        setIosVisible(false);
        return;
      }

      if (isIos() && !installEvent && iosInstallPromptAllowed()) {
        setIosVisible(true);
      }
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallEvent(event);
      setIosVisible(false);

      if (installPromptAllowed()) {
        setVisible(true);
      }
    };

    const handleAppInstalled = () => {
      setInstallEvent(null);
      setVisible(false);
      setIosVisible(false);
      clearInstallPromptDismissals();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('focus', refreshVisibility);
    document.addEventListener('visibilitychange', refreshVisibility);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('focus', refreshVisibility);
      document.removeEventListener('visibilitychange', refreshVisibility);
    };
  }, [installEvent]);

  const handleInstall = async () => {
    if (!installEvent) {
      return;
    }

    installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
    setVisible(false);
  };

  const handleLater = () => {
    snoozeInstallPrompt({
      untilKey: INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
    });
    setVisible(false);
  };

  const handleNeverAskAgain = () => {
    permanentlyDismissInstallPrompt({
      neverAskKey: INSTALL_PROMPT_NEVER_ASK_KEY,
      untilKey: INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
    });
    setVisible(false);
  };

  const handleIosLater = () => {
    snoozeInstallPrompt({
      untilKey: IOS_INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
    });
    setIosVisible(false);
  };

  const handleIosNeverAskAgain = () => {
    permanentlyDismissInstallPrompt({
      neverAskKey: IOS_INSTALL_PROMPT_NEVER_ASK_KEY,
      untilKey: IOS_INSTALL_PROMPT_SNOOZED_UNTIL_KEY,
    });
    setIosVisible(false);
  };

  if (!canPrompt && !canShowIosGuide) {
    return null;
  }

  const mode = canPrompt ? 'native' : 'ios';
  const later = mode === 'native' ? handleLater : handleIosLater;

  return (
    <Paper
      role="dialog"
      aria-label={t('pwa.install.ariaLabel')}
      elevation={4}
      sx={{
        position: 'fixed',
        insetInline: { xs: 16, sm: 'auto' },
        right: { sm: 24 },
        bottom: { xs: 92, sm: 24 },
        zIndex: (theme) => theme.zIndex.snackbar,
        maxWidth: 380,
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(255, 250, 239, 0.96)',
        backdropFilter: 'blur(18px)',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <BrandLogo compact emblemSize={44} />

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4">
            {mode === 'ios' ? t('pwa.install.iosTitle') : t('pwa.install.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {mode === 'ios' ? t('pwa.install.iosCopy') : t('pwa.install.copy')}
          </Typography>
          {mode === 'ios' ? (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <IosShareIcon color="primary" fontSize="small" />
                <Typography variant="body2">{t('pwa.install.iosShareStep')}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <AddToHomeScreenIcon color="primary" fontSize="small" />
                <Typography variant="body2">{t('pwa.install.iosAddStep')}</Typography>
              </Stack>
              <Button color="inherit" onClick={handleIosLater} sx={{ alignSelf: 'flex-start' }}>
                {t('pwa.install.later')}
              </Button>
              <Button
                color="inherit"
                onClick={handleIosNeverAskAgain}
                sx={{ alignSelf: 'flex-start' }}
              >
                {t('pwa.install.neverAskAgain')}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={handleInstall}>
                {t('pwa.install.action')}
              </Button>
              <Button color="inherit" onClick={handleLater}>
                {t('pwa.install.later')}
              </Button>
              <Button color="inherit" onClick={handleNeverAskAgain}>
                {t('pwa.install.neverAskAgain')}
              </Button>
            </Stack>
          )}
        </Box>

        <IconButton size="small" aria-label={t('pwa.install.dismiss')} onClick={later}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export default InstallPrompt;
